import React, { useState, useEffect } from "react";

import { Form, Button, Alert, Spinner, Card } from "react-bootstrap";

import RemoteConfigService from "services/RemoteConfigService";
import { remoteConfig } from "services/firebase";

/**
 * SettingsForm allows admin to update the analyticsUrl property
 * in Firebase Remote Config. This enables dynamic reconfiguration
 * of analytics endpoints without redeploying the app.
 */
function Settings() {
  // Track the current analyticsUrl value
  const [analyticsUrl, setAnalyticsUrl] = useState("");
  // Track loading and feedback states
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Fetch the current analyticsUrl from remote config on mount
  useEffect(() => {
    const value = remoteConfig?.getValue("analyticsUrl")?.asString();
    setAnalyticsUrl(value ?? "");
  }, []);

  // Handle form submission to update analyticsUrl
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess(false);

    try {
      RemoteConfigService.setValue("analyticsUrl", analyticsUrl);
      setSuccess(true);
    } catch (err) {
      setError("Failed to update analyticsUrl: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="my-4">
      <Card.Body>
        <Card.Title>Analytics URL Settings</Card.Title>
        <Card.Text>
          Update the analytics url remote config variable. This controls where analytics
          data is sent. <br />
          The <code>{`{sessionId}`}</code> placeholder will be replaced with the session ID.
        </Card.Text>
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="analyticsUrl">
              <Form.Label>Analytics URL</Form.Label>
              <Form.Control
                type="url"
                placeholder="https://your-analytics-endpoint.com"
                value={analyticsUrl}
                onChange={(e) => setAnalyticsUrl(e.target.value)}
                required
                disabled={saving}
              />
              <Form.Text className="text-muted">
                Enter the full URL for your analytics endpoint.
              </Form.Text>
            </Form.Group>
            <Button variant="primary" type="submit" className="mt-3" disabled={saving}>
              {saving ? (
                <>
                  Saving... <Spinner as="span" animation="border" size="sm" />
                </>
              ) : (
                "Update Analytics URL"
              )}
            </Button>
            {error && (
              <Alert variant="danger" className="mt-3">
                {error}
              </Alert>
            )}
            {success && (
              <Alert variant="success" className="mt-3">
                Analytics URL updated successfully.
              </Alert>
          )}
        </Form>
      </Card.Body>
    </Card>
  );
}

export default Settings;
